import { IEventParticipant } from '@azavista/planner-lib';
import { QrParticipantData } from '../event/interfaces';
import { IPrinter, IPrinterConfig } from '@azavista/planner-lib/lib/onsite-checkin/print-server-api';
import { IPrinterSettings } from './configuration-storage.service';

export const isValidQrParticipantData = (data: QrParticipantData): boolean => {
    const { f, l, p } = data;
    return !!(f && l && p );
};

/** Safely parse JSON with try-catch */
export const jsonParse = <T>(jsonString: string): T | undefined => {
    let result: T | undefined;

    try {
        result = JSON.parse(jsonString);
    } catch (e) {
        console.warn(e);
        return undefined;
    }

    return result;
};

export const jsonParseQrParticipant = (qrParticipantString: string) => {
    const data = jsonParse<QrParticipantData>(qrParticipantString);
    return data && isValidQrParticipantData(data) ? data : undefined;
};

/**
 * Get QR code from both QR code of string, and QR on Badge of JSON
 *
 * @param qrString content of the scan-result
 * @returns Participant `qr_on_badge`, or `participant_id`, or `undefined`
 */
export const getQrCodeFromQr = (qrString: string): string | undefined => {
    const parsedJson = jsonParse(qrString);
    const isJson = parsedJson != null && typeof parsedJson === 'object';
    if (isJson) {
        const { q, p } = jsonParseQrParticipant(qrString) ?? {};
        return q ?? p;
    }
    return qrString;
};

/** Get the JSON string of an `IEventParticipant` object  */
export const getQrCodeOnBadge = (participant: IEventParticipant, eventId: string): string =>
    JSON.stringify(getQrOnBadgeData(participant));

export const getQrOnBadgeData = (participant: IEventParticipant): QrParticipantData => ({
    f: participant.first_name,
    l: participant.last_name,
    p: participant.id,
});

export const getParticipantQrCode = (participant: Pick<IEventParticipant, 'id' | 'qrcode_on_badge'>): string => {
    return participant.qrcode_on_badge ? participant.qrcode_on_badge : participant.id;
}

export const getExecutionTimeCounter = () => {
    const startTime = performance.now();
    return () => performance.now() - startTime;
}

export const getDefaultTearOff = (printerConfig: Pick<IPrinterConfig, 'cutter'>) => {
    return printerConfig.cutter ? 30 : 0;
}

export function createSequentialProxy<T extends object>(
  target: T,
  methodsToSequence: (keyof T)[]
): T {
  let sequence = Promise.resolve();

  return new Proxy(target, {
    get(target, prop, receiver) {
      const originalValue = Reflect.get(target, prop, receiver);

      // Check if it's a function AND if it's in our sequence list
      if (typeof originalValue === 'function' && methodsToSequence.includes(prop as keyof T)) {
        return (...args: any[]) => {
          // Add to queue
          const nextInLine = sequence.then(() => {
            return originalValue.apply(target, args);
          });

          // Update tail (with error recovery)
          sequence = nextInLine.catch(() => {});

          return nextInLine;
        };
      }

      // Otherwise, return as-is (runs immediately/normally)
      return originalValue;
    }
  });
}

/**
 * Extracts DPI from a printer model string
 * @param {string} name
 * @returns {number|null}
 */
export function extractDPIFromPrinterName(name: string) {
    // Regex breakdown:
    // (\d+) : Capture one or more digits
    // -?    : Optional hyphen
    // dpi   : Matches the literal string "dpi"
    // i     : Case-insensitive flag
    const regex = /(\d+)-?dpi/i;
    const match = name.match(regex);

    if (match && match[1]) {
        return parseInt(match[1], 10);
    }

    return null; // Return null if no DPI is found
}

export const getPrinterSettingsFromStatus = (config?: IPrinterConfig | null, printer?: IPrinter): IPrinterSettings => ({
    dpi: config?.dpi_hardware ?? extractDPIFromPrinterName(printer?.name),
    cutter: !!config?.cutter,
});

export const getCombinedPrinterConfig = (printer: IPrinter, localConfig: IPrinterSettings, printerConfig: IPrinterConfig): IPrinterConfig => {
    const dpiFromName = extractDPIFromPrinterName(printer.name);
    return {
        ...(printerConfig || {}),
        ...(localConfig || {}),
        cutter: localConfig.cutter ?? !!printerConfig.cutter,
        dpi_hardware: localConfig.dpi ?? printerConfig.dpi_hardware ?? dpiFromName,
    }
};

/**
 *
 * @param filename
 * @param fileExtensionsToSkipCapitalize this is intended to guard the multi-dot file extension like `.tar.gz`. While single-dot file extension like `.zip` is already taken care
 * @returns
 */
export function getFormattedFileNameCapitalizeWords(
  filename: string,
  fileExtensionsToSkipCapitalize: string[] = []
): string {
  // 1. Lowercase the entire string to normalize it
  const lowerFilename = filename.toLowerCase();

  let baseName = lowerFilename;
  let extension = "";

  // 2. Sort custom extensions by length descending
  const sortedExtensions = [...fileExtensionsToSkipCapitalize].sort(
    (a, b) => b.length - a.length
  );

  // 3. Find if the file ends with a known multi-dot extension
  const matchedExt = sortedExtensions.find((ext) =>
    lowerFilename.endsWith(ext.toLowerCase())
  );

  if (matchedExt) {
    extension = lowerFilename.slice(-matchedExt.length);
    baseName = lowerFilename.slice(0, -matchedExt.length);
  } else {
    // Fallback: Use the last '.' as the extension boundary
    const lastDotIndex = lowerFilename.lastIndexOf(".");
    if (lastDotIndex > 0) {
      extension = lowerFilename.slice(lastDotIndex);
      baseName = lowerFilename.slice(0, lastDotIndex);
    }
  }

  // 4. Capitalize first character and any character after a non-alphanumeric character
  // u flag enables Unicode parsing
  const formattedBaseName = baseName.replace(
    /(^|[^\p{L}\p{N}])([\p{L}\p{N}])/gu,
    (match, nonRegularChar, charToCapitalize) => {
      // nonRegularChar is either the start of the string or the symbol (-, _, etc.)
      // charToCapitalize is the actual letter/number to convert to uppercase
      return nonRegularChar + charToCapitalize.toUpperCase();
    }
  );

  // 5. Combine the formatted base name with the lowercased/skipped extension
  return formattedBaseName + extension;
}

export const getFormattedUrlObject = (path: string, baseUrl?: string): URL => {
    try {
        const url = new URL(path, baseUrl);
        return url;
    } catch {
        if (path.split('/')[0].includes('.')) {
            path = 'https://' + path;
        }
        return getFormattedUrlObject(path, location.origin);
    }
}

export const getFormattedServerName = (path: string, baseUrl?: string) => {
    const removeTrailingSlash = (url: string): string => {
        return url.endsWith('/') ? url.slice(0, -1) : url;
    };
    const url = getFormattedUrlObject(path, baseUrl);
    return removeTrailingSlash(url.href);
};
